import { useTranslation } from "next-i18next";
import React, { useEffect } from "react";
import { useGetShowcaseStrategy, usePostCopyStrategy } from "@/api/strategyApi";
import { useUserContext } from "@/contexts/userContext";
import { randomString, useToastHelper } from "@/helpers/helperFunctions";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import { guideModalAtom } from "@/Atoms/modal/DialogueAtoms";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FaPlay } from "react-icons/fa";

function toYouTubeEmbed(url: string) {
  try {
    const parsedUrl = new URL(url);

    // Case 1: Standard YouTube link (https://www.youtube.com/watch?v=VIDEO_ID)
    if (parsedUrl.hostname.includes("youtube.com")) {
      const videoId = parsedUrl.searchParams.get("v");
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // Case 2: Short link (https://youtu.be/VIDEO_ID)
    if (parsedUrl.hostname.includes("youtu.be")) {
      const videoId = parsedUrl.pathname.replace("/", "");
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    return ""; // Not a valid YouTube link
  } catch (e) {
    return ""; // Invalid URL
  }
}

interface Props {
  handleSelect?: (strategy_id: number) => any;
  handleView?: (link: string) => any;
  onSelect?: () => any;
}

const StrategyTemplate: React.FC<Props> = ({
  handleSelect,
  handleView,
  onSelect,
}) => {
  const { user } = useUserContext();
  const { details } = useAppDetails();
  const { data = [] } = useGetShowcaseStrategy(user?.user_id, details?.id);
  const { toast } = useToastHelper();
  const router = useRouter();
  const [showGuideModal, setShowGuideModal] = useRecoilState(guideModalAtom);

  const {
    mutateAsync: copyStrategy,
    data: copyStrategyData,
    isSuccess: isCopySuccess,
    isPending: isCopyPending,
  } = usePostCopyStrategy();

  const defaultHandleSelect = async (strategy_id: string) => {
    if (!user?.user_id) return;

    try {
      await copyStrategy({
        userId: user?.user_id,
        key: strategy_id,
      });
    } catch {
      toast({
        title: "Error copying strategy",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (isCopySuccess && copyStrategyData && user?.user_id) {
      toast({
        title: "Strategy copied successfully",
        variant: "default",
      });
      setTimeout(() => {
        router.push(
          "/edit-strategy/" +
            encodeURIComponent(user?.user_id) +
            "/" +
            copyStrategyData?.data?.id +
            randomString(5) +
            "/" +
            user.token +
            "/false" +
            `?viewonly=true&spotlight=${true}&ref=${router.asPath}&locale=${
              router.query.locale || "en"
            }`,
        );
      }, 1000);
    }
  }, [isCopySuccess, isCopyPending]);

  return (
    <div className="w-full max-w-screen  mx-auto px-4 h-[80vh] flex flex-col">
      {/* Header */}
      <div className="text-left  space-y-2">
        <h2 className="text-xl font-semibold">
          Start with a strategy template{" "}
        </h2>
        <p className="text-base text-muted-foreground">
          See what’s possible before you build your own. We’ll walk you through
          a{" "}
          <span className="text-[--accent-secondary] font-bold">
            {" "}
            prebuilt strategy in view-only mode{" "}
          </span>
          so you can see what’s possible.
        </p>
        <p className="text-base font-bold">
          Select a template to begin the tour.
        </p>
      </div>

      {/* Strategy Cards */}
      <div className="grid grid-cols-1 w-full justify-center  sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-6 overflow-y-auto overflow-x-hidden p-2">
        {// @ts-ignore
        data?.data?.map((strategy: any) => {
          const imageName = strategy?.image_url;
          if (imageName) {
            return (
              <div
                key={strategy.strategy_key}
                className="relative flex min-h-40 aspect-video flex-col h-full duration-300 ease-in-out select-none shadow-none rounded-lg overflow-hidden"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageName}
                  alt={strategy.title}
                  className="w-auto h-full min-h-40  "
                />

                {/* Invisible Buttons Container */}
                <div className="absolute bottom-[3%] left-0 w-full h-[15%] text-xs flex justify-between gap-4 px-[5%] z-10">
                  <div
                    onClick={() =>
                      strategy?.video &&
                      setShowGuideModal({
                        isOpen: true,
                        link: toYouTubeEmbed(strategy.video),
                      })
                    }
                    className="w-[65%]  gap-1 cursor-pointer h-full text-muted-foreground flex whitespace-nowrap items-center text-xs "
                    title="Quick Strategy Walkthrough"
                  >
                    <FaPlay />
                    Quick Strategy Walkthrough
                  </div>

                  <Button
                    onClick={() => {
                      handleSelect
                        ? handleSelect(strategy?.strategy_key)
                        : defaultHandleSelect(strategy?.strategy_key);
                      onSelect?.();
                    }}
                    className="w-[35%] h-full py- px-2 cursor-pointer bg-[--accent-primary] text-xs  hover:bg-[--accent-primary] text-white"
                    title="Select"
                  >
                    Select
                  </Button>
                </div>
              </div>
            );
          }

          return (
            <Card
              key={strategy.strategy_key}
              className="flex flex-col justify-between h-full shadow hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] rounded-2xl transition-all duration-300"
            >
              <CardContent className="p-4 flex flex-col gap-3 h-full">
                <div className="text-base font-bold">{strategy.title}</div>
                <div className="text-xs text-muted-foreground">
                  <strong className="text-[--accent-primary]">Overview:</strong>{" "}
                  {strategy.overview}
                </div>
                <div className="text-xs text-muted-foreground">
                  <strong className="text-[--accent-primary]">
                    Why it works:
                  </strong>{" "}
                  {strategy.why}
                </div>

                <div className="flex items-center justify-between mt-auto pt-3 border-t border-muted text-xs">
                  <div
                    onClick={() =>
                      strategy?.video &&
                      setShowGuideModal({
                        isOpen: true,
                        link: toYouTubeEmbed(strategy.video),
                      })
                    }
                    className="text-[--accent-primary] cursor-pointer italic flex items-center gap-2 hover:underline"
                  >
                    <FaPlay /> Quick Strategy Walkthrough
                  </div>
                  <Button
                    onClick={() => {
                      handleSelect
                        ? handleSelect(strategy?.strategy_key)
                        : defaultHandleSelect(strategy?.strategy_key);
                      onSelect?.();
                    }}
                    className="h-8 px-4 text-xs bg-tl2-blue-base text-white hover:bg-tl2-blue-semidark rounded-md"
                  >
                    Select
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default StrategyTemplate;
